import { Dispatch } from 'redux';
import callApi, { CallApiResult } from 'src/api';
import { retrieveOfferDocument } from 'src/api/cart';
import { CartOfferRequestDTO } from 'src/entities/cart/request/CartOfferRequestDTO';
import { enqueueSnackbarAction } from 'src/store/notifications/actions';

/**
 * Submit Offer API Request
 */
export const submitOffer = (data: CartOfferRequestDTO): Promise<CallApiResult> => {
    return callApi('/api/officeProfiOrder/offer', { method: 'PUT', data });
};

/**
 * Submit Offer API Request
 */
export const submitAndPayOffer = (data: CartOfferRequestDTO): Promise<CallApiResult> => {
    return callApi('/api/officeProfiOrder/offerAndPay', { method: 'PUT', data });
};

export const fetchOfferPaymentStatus = async (orderId: string): Promise<boolean> => {
    const response = await callApi(`/api/officeProfiOrder/${orderId}/paymentStatus`);
    return Boolean(response?.response?.data);
};

export interface OfferCommission {
    possibleRebate: number; // gross office-profi commission on the offer's goods
    officeProfiRebate: number; // rebate granted to the customer from that commission
}

/**
 * Office-profi commission breakdown for one offer. Reuses the ordering-api commission calculator
 * (the same one the shopping-cart offer flow uses), so no /ordering/calc round-trip is needed.
 */
export const fetchOfferCommission = async (publicId: string): Promise<OfferCommission | undefined> => {
    const response = await callApi(`/api/officeProfiOrder/${publicId}/commission`);
    return response?.response?.data as OfferCommission | undefined;
};

/**
 * Fetch my offers
 */
export const fetchMyOffers = (): Promise<CallApiResult> => {
    return callApi('/api/officeProfiOrder/current/myOffers');
};

/**
 * Fetch offers
 */
export const fetchOffers = (): Promise<CallApiResult> => {
    return callApi('/api/officeProfiOrder/current/offers');
};

export const cancelOffer = (publicId: string): Promise<CallApiResult> => {
    return callApi(`/api/officeProfiOrder/${publicId}/cancel`, { method: 'POST' });
};

/** R57 Q893: whether a held partner role allows „Angebot erstellen". */
export const fetchOfferPermission = (): Promise<CallApiResult> => {
    return callApi('/api/officeProfiOrder/offerPermission');
};

/** R57 Q893 „Für Kunde freigeben": the customer the offer was built for sees and orders it. */
export const releaseOffer = (publicId: string): Promise<CallApiResult> => {
    return callApi(`/api/officeProfiOrder/${publicId}/offered`, { method: 'POST' });
};

/** R57 Q893 „Freigabe zurücknehmen": the offer becomes a draft again, hidden from the customer. */
export const withdrawOffer = (publicId: string): Promise<CallApiResult> => {
    return callApi(`/api/officeProfiOrder/${publicId}/new`, { method: 'POST' });
};

/**
 * Downloads an office-profi offer PDF and triggers a browser save. On failure shows an error snackbar.
 * Shared by the partner "My Offers" page and the org-dashboard offers menu / details dialog.
 */
export const downloadOfferPdf = (publicId: string, dispatch: Dispatch): void => {
    retrieveOfferDocument(publicId)
        .then(({ error, response }) => {
            if (error) {
                dispatch(
                    enqueueSnackbarAction({
                        message: error.message,
                        options: { variant: 'error', key: 'offer-download-error' },
                    })
                );
                return;
            }
            if (response) {
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `offer-${publicId}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        })
        .catch(console.error);
};

/** R25-9: the coffee sorts offered with an offer — the shop's products of a product group. */
export const fetchCoffeeSorts = (productGroup: string): Promise<CallApiResult> => {
    return callApi('/api/products', { headers: { productGroup } });
};
